/* eslint-disable react/no-unescaped-entities */
import React, { useState } from "react";
import { api } from "../../lib/api";
import { Modal, Field, AvatarUpload } from "../common";
import { Plus } from "lucide-react";

const CH_TYPES = [
  { v: "private", l: "CONVERSATION PRIVÉE" },
  { v: "group", l: "GROUPE" },
  { v: "info_descending", l: "CANAL D'INFORMATION (DESCENDANT)" },
  { v: "info_interactive", l: "CANAL D'INFORMATION (INTERACTIF)" },
  { v: "ad", l: "CANAL DE PUBLICITÉ" },
];

function Checklist({ items, selected, toggle, empty }) {
  return (
    <div className="max-h-40 overflow-auto frame p-2 space-y-1">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 px-1 py-1 cursor-pointer hover:bg-[color:var(--r-primary)]/8">
          <input type="checkbox" checked={selected.includes(it.id)} onChange={() => toggle(it.id)} />
          <span className="text-sm">{it.name}</span>
        </label>
      ))}
      {items.length === 0 && <div className="sys-label">{empty}</div>}
    </div>
  );
}

export function NewChannelModal({ open, onClose, campaignId, characters, npcs, onCreated }) {
  const [type, setType] = useState("private");
  const [name, setName] = useState("");
  const [chars, setChars] = useState([]);
  const [selNpcs, setSelNpcs] = useState([]);
  const tog = (arr, set) => (id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  async function create() {
    if (!name.trim()) return;
    const r = await api.post(`/campaigns/${campaignId}/channels`, {
      type, name: name.trim(), character_ids: chars, npc_ids: selNpcs,
    });
    onCreated?.(r.data);
    setName(""); setChars([]); setSelNpcs([]); setType("private");
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="NOUVELLE TRANSMISSION" width={480}>
      <Field label="TYPE DE CANAL">
        <select data-testid="channel-type-select" className="input font-mono text-xs" value={type} onChange={(e) => setType(e.target.value)}>
          {CH_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
      </Field>
      <Field label="NOM">
        <input data-testid="channel-name-input" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vincenzo / FAMIGLIA / TVN // DIRECT" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="sys-label block mb-1.5">PERSONNAGES</span>
          <Checklist items={characters} selected={chars} toggle={tog(chars, setChars)} empty="AUCUN" />
        </div>
        <div>
          <span className="sys-label block mb-1.5">{type === "ad" ? "ANNONCEUR (PNJ)" : "IDENTITÉS PNJ"}</span>
          <Checklist items={npcs.filter((n) => n.is_active)} selected={selNpcs} toggle={tog(selNpcs, setSelNpcs)} empty="AUCUN" />
        </div>
      </div>
      {type === "ad" && <div className="sys-label mt-2" style={{ textTransform: "none", letterSpacing: "normal" }}>Les joueurs sélectionnés reçoivent les annonces en lecture seule. Ajoutez un PNJ comme annonceur, puis publiez vos encarts (texte + visuel).</div>}
      <button data-testid="channel-create-submit" className="btn btn-solid w-full mt-4" onClick={create}>CRÉER LE CANAL</button>
    </Modal>
  );
}

export function NewNpcModal({ open, onClose, campaignId, onCreated, orgs = [], onOrgAdded }) {
  const [f, setF] = useState({ name: "", function: "", organization: "", type: "npc", fictional_id: "", avatar: "", status: "en ligne", repla_id: "", welcome_script: "" });
  const [newOrg, setNewOrg] = useState("");
  const [addingOrg, setAddingOrg] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function addOrg() {
    if (!newOrg.trim()) return;
    const r = await api.post(`/campaigns/${campaignId}/organizations`, { name: newOrg.trim() });
    setF({ ...f, organization: r.data.name });
    setNewOrg(""); setAddingOrg(false);
    onOrgAdded?.();
  }

  async function create() {
    if (!f.name.trim()) return;
    const r = await api.post(`/campaigns/${campaignId}/npcs`, { ...f, repla_id: f.repla_id.trim().toUpperCase() });
    onCreated?.(r.data);
    setF({ name: "", function: "", organization: "", type: "npc", fictional_id: "", avatar: "", status: "en ligne", repla_id: "" });
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="NOUVEAU PNJ">
      <Field label="AVATAR"><AvatarUpload value={f.avatar} name={f.name} onChange={(p) => setF({ ...f, avatar: p })} /></Field>
      <Field label="NOM"><input data-testid="npc-name-input" className="input" value={f.name} onChange={set("name")} autoFocus /></Field>
      <Field label="TYPE D'IDENTITÉ">
        <select className="input font-mono text-xs" value={f.type} onChange={set("type")}>
          <option value="npc">PNJ</option>
          <option value="institution">INSTITUTION</option>
          <option value="anonymous">ANONYME / INCONNU</option>
          <option value="news_source">SOURCE D'INFORMATION</option>
        </select>
      </Field>
      <Field label="FONCTION"><input className="input" value={f.function} onChange={set("function")} placeholder="Consigliere" /></Field>
      <Field label="STATUT"><input data-testid="npc-status-input" className="input" value={f.status} onChange={set("status")} placeholder="en ligne" /></Field>
      <Field label="ORGANISATION / FACTION">
        <div className="flex gap-2">
          <select data-testid="npc-org-select" className="input font-mono text-xs" value={f.organization} onChange={set("organization")}>
            <option value="">— Aucune —</option>
            {orgs.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
          </select>
          <button type="button" data-testid="npc-org-add-toggle" onClick={() => setAddingOrg((v) => !v)} className="btn btn-ghost px-3"><Plus size={14} /></button>
        </div>
        {addingOrg && (
          <div className="flex gap-2 mt-2">
            <input data-testid="npc-org-new-input" className="input" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} placeholder="Nouvelle organisation" />
            <button type="button" data-testid="npc-org-add-submit" className="btn btn-solid px-3 text-[10px]" onClick={addOrg}>AJOUTER</button>
          </div>
        )}
      </Field>
      <Field label="IDENTIFIANT FICTIF"><input className="input font-mono" value={f.fictional_id} onChange={set("fictional_id")} placeholder="NODE-0001-XXX" /></Field>
      <Field label="ID REPLA (optionnel — laisser vide = généré)"><input data-testid="npc-replaid-input" className="input font-mono" value={f.repla_id} onChange={(e) => setF({ ...f, repla_id: e.target.value.toUpperCase() })} placeholder="EX : CONSIG-07" /></Field>
      <Field label="MESSAGE D'ACCUEIL (si contacté via son ID — optionnel)"><textarea data-testid="npc-welcome-input" className="input" rows={2} value={f.welcome_script} onChange={set("welcome_script")} placeholder="Ex : Qui êtes-vous ? Comment avez-vous eu ce numéro ?" /></Field>
      <button data-testid="npc-create-submit" className="btn btn-solid w-full mt-2" onClick={create}>CRÉER L'IDENTITÉ</button>
    </Modal>
  );
}

export function EditNpcModal({ open, onClose, npc, campaignId, orgs = [], onOrgAdded, onSaved }) {
  const [f, setF] = useState(npc || {});
  const [newOrg, setNewOrg] = useState("");
  const [addingOrg, setAddingOrg] = useState(false);
  React.useEffect(() => { setF(npc || {}); }, [npc]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function addOrg() {
    if (!newOrg.trim()) return;
    const r = await api.post(`/campaigns/${campaignId}/organizations`, { name: newOrg.trim() });
    setF({ ...f, organization: r.data.name });
    setNewOrg(""); setAddingOrg(false);
    onOrgAdded?.();
  }
  async function save() {
    await api.patch(`/npcs/${npc.id}`, {
      name: f.name, avatar: f.avatar || "", function: f.function || "", organization: f.organization || "",
      status: f.status || "", fictional_id: f.fictional_id || "", type: f.type || "npc",
      welcome_script: f.welcome_script || "",
    });
    onSaved?.();
    onClose();
  }

  if (!npc) return null;
  return (
    <Modal open={open} onClose={onClose} title="MODIFIER LE PNJ">
      <Field label="AVATAR"><AvatarUpload value={f.avatar} name={f.name} onChange={(p) => setF({ ...f, avatar: p })} /></Field>
      <Field label="NOM"><input data-testid="edit-npc-name" className="input" value={f.name || ""} onChange={set("name")} /></Field>
      <Field label="TYPE D'IDENTITÉ">
        <select className="input font-mono text-xs" value={f.type || "npc"} onChange={set("type")}>
          <option value="npc">PNJ</option>
          <option value="institution">INSTITUTION</option>
          <option value="anonymous">ANONYME / INCONNU</option>
          <option value="news_source">SOURCE D'INFORMATION</option>
        </select>
      </Field>
      <Field label="FONCTION"><input className="input" value={f.function || ""} onChange={set("function")} /></Field>
      <Field label="ORGANISATION / FACTION">
        <div className="flex gap-2">
          <select data-testid="edit-npc-org-select" className="input font-mono text-xs" value={f.organization || ""} onChange={set("organization")}>
            <option value="">— Aucune —</option>
            {orgs.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
            {f.organization && !orgs.find((o) => o.name === f.organization) && <option value={f.organization}>{f.organization}</option>}
          </select>
          <button type="button" onClick={() => setAddingOrg((v) => !v)} className="btn btn-ghost px-3"><Plus size={14} /></button>
        </div>
        {addingOrg && (
          <div className="flex gap-2 mt-2">
            <input className="input" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} placeholder="Nouvelle organisation" />
            <button type="button" className="btn btn-solid px-3 text-[10px]" onClick={addOrg}>AJOUTER</button>
          </div>
        )}
      </Field>
      <Field label="STATUT"><input className="input" value={f.status || ""} onChange={set("status")} placeholder="en ligne" /></Field>
      <Field label="IDENTIFIANT FICTIF"><input className="input font-mono" value={f.fictional_id || ""} onChange={set("fictional_id")} /></Field>
      <Field label="MESSAGE D'ACCUEIL (si contacté via son ID — optionnel)"><textarea data-testid="edit-npc-welcome" className="input" rows={2} value={f.welcome_script || ""} onChange={set("welcome_script")} placeholder="Ex : Qui êtes-vous ? Comment avez-vous eu ce numéro ?" /></Field>
      <button data-testid="edit-npc-save" className="btn btn-solid w-full mt-2" onClick={save}>ENREGISTRER</button>
    </Modal>
  );
}

export function EditCharacterModal({ open, onClose, character, onSaved }) {
  const [f, setF] = useState(character || {});
  const [newPwd, setNewPwd] = useState("");
  React.useEffect(() => { setF(character || {}); setNewPwd(""); }, [character]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save() {
    await api.patch(`/characters/${character.id}`, {
      name: f.name, avatar: f.avatar || "", pseudonym: f.pseudonym || "",
      status: f.status || "", description: f.description || "", fictional_id: f.fictional_id || "",
    });
    if (character.user_id && newPwd.trim().length >= 4) {
      await api.patch(`/characters/${character.id}/password`, { password: newPwd.trim() });
    }
    onSaved?.();
    onClose();
  }
  if (!character) return null;
  return (
    <Modal open={open} onClose={onClose} title="MODIFIER LE PERSONNAGE">
      <Field label="AVATAR"><AvatarUpload value={f.avatar} name={f.name} onChange={(p) => setF({ ...f, avatar: p })} /></Field>
      <Field label="NOM"><input data-testid="edit-char-name" className="input" value={f.name || ""} onChange={set("name")} /></Field>
      <Field label="PSEUDONYME"><input className="input" value={f.pseudonym || ""} onChange={set("pseudonym")} /></Field>
      <Field label="STATUT"><input className="input" value={f.status || ""} onChange={set("status")} placeholder="en ligne" /></Field>
      <Field label="IDENTIFIANT FICTIF"><input className="input font-mono" value={f.fictional_id || ""} onChange={set("fictional_id")} /></Field>
      <Field label="DESCRIPTION"><textarea className="input" rows={2} value={f.description || ""} onChange={set("description")} /></Field>
      {character.user_id && (
        <Field label="NOUVEAU MOT DE PASSE JOUEUR">
          <input data-testid="edit-char-password" type="text" className="input font-mono" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="laisser vide pour ne pas changer" />
        </Field>
      )}
      <button data-testid="edit-char-save" className="btn btn-solid w-full mt-2" onClick={save}>ENREGISTRER</button>
    </Modal>
  );
}

export function NewCharacterModal({ open, onClose, campaignId, onCreated }) {
  const [f, setF] = useState({ name: "", player_password: "", fictional_id: "", avatar: "", repla_id: "" });
  const [withAccount, setWithAccount] = useState(true);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function create() {
    if (!f.name.trim()) return;
    const payload = {
      name: f.name, avatar: f.avatar, fictional_id: f.fictional_id,
      create_account: withAccount,
      player_password: withAccount ? (f.player_password || "joueur123") : "",
      repla_id: f.repla_id.trim().toUpperCase(),
    };
    const r = await api.post(`/campaigns/${campaignId}/characters`, payload);
    onCreated?.(r.data);
    setF({ name: "", player_password: "", fictional_id: "", avatar: "", repla_id: "" });
    setWithAccount(true);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="NOUVEAU PERSONNAGE JOUEUR">
      <Field label="AVATAR"><AvatarUpload value={f.avatar} name={f.name} onChange={(p) => setF({ ...f, avatar: p })} /></Field>
      <Field label="NOM DU PERSONNAGE"><input data-testid="character-name-input" className="input" value={f.name} onChange={set("name")} autoFocus /></Field>
      <Field label="ID REPLA (optionnel — laisser vide = généré)"><input data-testid="character-replaid-input" className="input font-mono" value={f.repla_id} onChange={(e) => setF({ ...f, repla_id: e.target.value.toUpperCase() })} placeholder="EX : AGENT-NOX" /></Field>

      <div className="mt-2 mb-1 p-3" style={{ border: "1px solid color-mix(in srgb, var(--r-primary) 20%, transparent)", background: "color-mix(in srgb, var(--r-primary) 5%, transparent)" }}>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input data-testid="character-with-account" type="checkbox" checked={withAccount} onChange={(e) => setWithAccount(e.target.checked)} />
          <span className="sys-label" style={{ textTransform: "none", letterSpacing: "normal" }}>Créer un accès joueur (connexion)</span>
        </label>
        {withAccount && (
          <>
            <div className="sys-label mt-3 mb-2" style={{ textTransform: "none", letterSpacing: "normal", color: "var(--r-text-muted)" }}>
              Un <span style={{ color: "var(--r-primary)" }}>ID REPLA</span> unique sera généré automatiquement. Il servira à la fois d'identifiant de connexion et de numéro joignable (visible ensuite dans GÉRER PJ).
            </div>
            <Field label="MOT DE PASSE">
              <input data-testid="character-password-input" className="input font-mono" value={f.player_password} onChange={set("player_password")} placeholder="défaut : joueur123" />
            </Field>
          </>
        )}
      </div>

      <Field label="IDENTIFIANT FICTIF (narratif, optionnel)"><input className="input font-mono" value={f.fictional_id} onChange={set("fictional_id")} placeholder="ex : matricule, pseudo in-game…" /></Field>
      <button data-testid="character-create-submit" className="btn btn-solid w-full mt-2" onClick={create}>CRÉER LE PERSONNAGE</button>
    </Modal>
  );
}
